import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import ClaimPhoneForm from '../components/ClaimPhoneForm';
import {
  getPhoneNumber,
  getReportsForOwner,
  sendVerificationCode,
  verifyOwnershipCode,
  updateNumberSettings,
} from '../services/firestoreService';
import { formatInternationalDisplay } from '../utils/phoneCanonical';

function UserDashboard() {
  const { user } = useAuth();
  const [ownedNumbers, setOwnedNumbers] = useState([]);
  const [reports, setReports] = useState([]);
  const [verifyCode, setVerifyCode] = useState('');
  const [selectedVerify, setSelectedVerify] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const owned = userDoc.data()?.ownedNumbers || [];

      const phoneDetails = await Promise.all(
        owned.map(async (id) => {
          const p = await getPhoneNumber(id);
          return p ? { ...p, canonicalId: id } : { canonicalId: id, displayE164: `+${id}` };
        })
      );
      setOwnedNumbers(phoneDetails);
      setReports(await getReportsForOwner(owned));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const handleClaimed = (result) => {
    if (result?.alreadyClaimed && result?.status === 'verified') {
      setMessage('You already own and have verified this number.');
    } else if (result?.alreadyClaimed) {
      setMessage('You already claimed this number. Send a verification code to confirm ownership.');
    } else {
      setMessage('Number claimed. Click “Send verification code” below, then enter the code shown.');
    }
    setSelectedVerify(result?.canonicalId || '');
    load();
  };

  const handleSendCode = async (canonicalId) => {
    setError('');
    setMessage('');
    try {
      const res = await sendVerificationCode(canonicalId);
      setMessage(res.message || 'Verification code sent.');
      if (res.code) setVerifyCode(res.code);
      setSelectedVerify(canonicalId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await verifyOwnershipCode(selectedVerify, verifyCode);
      setMessage('Number verified successfully.');
      setVerifyCode('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSettings = async (canonicalId, updates) => {
    try {
      await updateNumberSettings(canonicalId, updates);
      setMessage('Settings updated.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBusinessNameBlur = async (canonicalId, value) => {
    try {
      await updateNumberSettings(canonicalId, {
        businessName: value.trim() || null,
      });
      setMessage('Business name updated.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-2xl font-bold mb-6">Your Dashboard</h1>

      {message && <p className="mb-4 text-green-700 bg-green-50 p-3 rounded">{message}</p>}
      {error && <p className="mb-4 text-red-600 bg-red-50 p-3 rounded">{error}</p>}

      <section className="mb-8 p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">Claim a phone number</h2>
        <ClaimPhoneForm onClaimed={handleClaimed} />
      </section>

      <section className="mb-8 p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">Your numbers</h2>
        {ownedNumbers.length === 0 ? (
          <p className="text-gray-500">
            You have not claimed any numbers yet. Claim a number above, or open a number page and
            use “Claim this number”.
          </p>
        ) : (
          <ul className="space-y-4">
            {ownedNumbers.map((phone) => (
              <li key={phone.canonicalId} className="border rounded p-4">
                <div className="flex justify-between items-start">
                  <Link
                    to={`/number/${phone.canonicalId}`}
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    {formatInternationalDisplay(phone) ||
                      phone.displayE164 ||
                      `+${phone.canonicalId}`}
                  </Link>
                  <span className="text-sm text-gray-500">
                    {phone.verified ? 'Verified' : 'Pending verification'}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {!phone.verified && (
                    <button
                      type="button"
                      onClick={() => handleSendCode(phone.canonicalId)}
                      className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
                    >
                      Send verification code
                    </button>
                  )}
                  <select
                    value={phone.type || 'unknown'}
                    onChange={(e) =>
                      handleSettings(phone.canonicalId, { type: e.target.value })
                    }
                    className="text-sm border rounded px-2 py-1"
                    disabled={!phone.verified}
                    title={phone.verified ? '' : 'Verify ownership to change settings'}
                  >
                    <option value="personal">Personal</option>
                    <option value="business">Business</option>
                    <option value="unknown">Unknown</option>
                  </select>
                  <label className="text-sm flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={phone.isPrivate || false}
                      disabled={!phone.verified}
                      onChange={(e) =>
                        handleSettings(phone.canonicalId, { isPrivate: e.target.checked })
                      }
                    />
                    Private (hide public reports)
                  </label>
                </div>
                {phone.verified && (phone.type === 'business' || phone.businessName) && (
                  <div className="mt-3">
                    <label className="block text-sm text-gray-600 mb-1">
                      Business name (shown on listing when verified)
                    </label>
                    <input
                      type="text"
                      defaultValue={phone.businessName || ''}
                      maxLength={120}
                      className="w-full text-sm border rounded px-3 py-2"
                      placeholder="e.g. Your company name"
                      onBlur={(e) =>
                        handleBusinessNameBlur(phone.canonicalId, e.target.value)
                      }
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedVerify && (
        <section className="mb-8 p-6 bg-white rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">
            Verify +{selectedVerify}
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            Click “Send verification code” on your number, then enter the 6-digit code shown in the
            message (SMS is not wired up yet).
          </p>
          <form onSubmit={handleVerify} className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              className="border rounded px-3 py-2 w-36"
              maxLength={6}
            />
            <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              Verify
            </button>
          </form>
        </section>
      )}

      <section className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">Reports on your numbers</h2>
        {reports.length === 0 ? (
          <p className="text-gray-500">No reports yet.</p>
        ) : (
          <ul className="space-y-2">
            {reports.map((r) => (
              <li key={r.id} className="p-3 bg-gray-50 rounded text-sm">
                <Link to={`/number/${r.phoneNumberId}`} className="text-blue-600">
                  +{r.phoneNumberId}
                </Link>
                <span className="mx-2 text-gray-400">·</span>
                <span className="capitalize">{r.status}</span>
                <p className="mt-1 text-gray-600">{r.comment}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default UserDashboard;
